import { type SEOHandle } from "@nasa-gcn/remix-seo";
import * as E from "@react-email/components";
import {
  type ActionFunctionArgs,
  type HeadersFunction,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useFetcher,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import {
  CalendarDays,
  ChevronRight,
  Heart,
  HeartOff,
  Share2,
  ShieldCheck,
  User2,
  BatteryFull,
  Car,
  ArrowLeft,
  Clock,
  Zap,
  Upload,
  Award,
  Route,
  MapPin,
  Compass,
  Receipt,
  Wrench,
  UserCheck,
  TrendingUp,
  ExternalLink,
  Maximize2,
  X,
  ChevronLeft,
  CheckCircle,
  Unplug, // Added for success message
  BatteryCharging, // Added for battery condition
  PlugZap, // Added for charger condition
  Loader2, // Add Loader2
} from "lucide-react";
import { Img } from 'openimg/react'
import { QRCodeCanvas } from "qrcode.react";
import { useState, useEffect, useCallback } from "react";
import Markdown from "react-markdown";
import { useSwipeable } from "react-swipeable"; // Import the hook
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { serverOnly$ } from "vite-env-only/macros";

import { z } from "zod";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import { PriceHistoryChart } from "#app/components/price-history-chart";
import Station from "#app/components/station.tsx";
import { AspectRatio } from "#app/components/ui/aspect-ratio.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Card, CardContent } from "#app/components/ui/card.tsx";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "#app/components/ui/carousel.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "#app/components/ui/dialog.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { Input } from "#app/components/ui/input.tsx";
import { Label } from "#app/components/ui/label.tsx";
import { Separator } from "#app/components/ui/separator.tsx";
import { Skeleton } from "#app/components/ui/skeleton.tsx";
import { Slider } from "#app/components/ui/slider.tsx";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableHeader,
} from "#app/components/ui/table.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "#app/components/ui/tabs.tsx";
import { Textarea } from "#app/components/ui/textarea.tsx";
import { useToast } from "#app/hooks/use-toast.ts";
import { getUserId, requireUserId } from "#app/utils/auth.server.js";
import { getConnectorInfo } from "#app/utils/connectors.ts";
import { prisma } from "#app/utils/db.server.ts";
import { sendEmail } from "#app/utils/email.server.ts";
import { formatState, formatText } from "#app/utils/format-text.tsx";
import { checkHoneypot } from "#app/utils/honeypot.server.ts";
import {
  getUserImgSrc,
  getCdnCarImgSrc,
  getCdnUserImgSrc,
} from "#app/utils/misc.tsx";
import { cn } from "#app/utils/misc.tsx.js";
import {
  // combineServerTimings,
  makeTimings,
  time,
} from "#app/utils/timing.server.ts";
import { useOptionalUser } from "#app/utils/user.ts";

// Define the possible shapes of the action data
type ActionData =
  | { response: "success"; intent: "contactSeller" }
  | {
      response: "error";
      error: string;
      intent?: "contactSeller" | "add-to-garage";
    } // intent might be missing in generic error
  | { response: "error"; error: string } // Generic error without intent
  | undefined; // If no action ran or redirected

export async function loader({ request, params }: LoaderFunctionArgs) {
  // Add params here
  const timings = makeTimings("car loader");
  const carIdParam = params.carId; // Get carId from route params

  // Extract the numeric ID from the combined URL-like string
  // Example: 'volkswagen-id4-pro-s-2023-12345' -> '12345'
  // Adjust regex if your URL structure is different
  const match = carIdParam?.match(/-(\d+)$/);
  const carId = match ? parseInt(match[1], 10) : NaN;

  if (isNaN(carId)) {
    // Or redirect to /carros, depending on desired behavior for invalid formats
    throw new Response("Invalid Car ID format", { status: 400 });
  }

  const car = await time(
    () =>
      prisma.car.findUnique({
        where: { id: carId }, // Use the parsed integer ID
        select: {
          id: true,
          maker: true,
          model: true,
          version: true,
          price: true,
          views: true, // Selected for potential display, not needed for increment
          contacts: true, // Selected for potential display
          fabricationYear: true,
          modelYear: true,
          odometer: true,
          color: true,
          isIpvaPaid: true,
          isUniqueOwner: true,
          isWarrantyActive: true,
          isRevisionOk: true,
          isBatteryOk: true, // Added for battery condition
          isOriginalChargerIncluded: true, // Added for charger condition
          isInactive: true,
          thirdPartyUrl: true,
          content: true,
          phone: true,
          email: true, // Owner's email for contact form logic
          createdAt: true,
          updatedAt: true,
          city: true,
          state: true,
          url: true,
          // Add boolean feature flags
          hasAutopilot: true,
          hasFsd: true,
          hasSunroof: true,
          hasHud: true,
          hasDualMotor: true,
          hasPremiumInterior: true,
          hasAwd: true,
          hasPremiumSound: true,
          hasHeatedSeats: true,
          hasHeatedSteeringWheel: true,
          hasChargingCables: true, // Corresponds to "Carregador Rápido Incluso" ? Maybe rename mapping later
          has360Camera: true,
          hasVoiceAssistant: true,
          hasSmartEntry: true,
          hasElectricTrunk: true,
          // End boolean feature flags
          vehicleAttributes: { select: { name: true } },
          owner: { select: { username: true, image: true, email: true } }, // Fetch owner email if needed for sending
          images: { select: { id: true, objectKey: true }},
          userCar: {
            select: {
              id: true,
              name: true,
              range: true,
              batteryCapacity: true,
              compatibleOutlets: { select: { connector: true, power: true } },
            },
          },
          subtype: {
            select: {
              id: true,
              name: true,
              range: true,
              batteryCapacity: true,
              compatibleOutlets: { select: { connector: true, power: true } },
            },
          },
        },
      }),
    { timings, type: "Car loader" },
  );

  if (!car) {
    throw new Response(null, {
      status: 301,
      headers: {
        Location: "/carros",
      },
    });
  }

  // --- Increment View Count (Fire-and-forget) ---
  prisma.car
    .update({
      where: { id: car.id },
      data: { views: { increment: 1 } },
    })
    .catch((error) => {
      // Log the error, but don't block the page load
      console.error(`Failed to increment view count for car ${car.id}:`, error);
      // Consider sending this to a proper logging service
    });
  // --- End Increment View Count ---

  const similarCars = await time(
    () =>
      prisma.car.findMany({
        where: {
          AND: [
            { maker: car.maker },
            { model: car.model },
            { id: { not: car.id } }, // Use car.id directly
            { isInactive: false },
          ],
        },
        select: {
          // Select necessary fields for similar cars
          id: true,
          maker: true,
          model: true,
          fabricationYear: true,
          modelYear: true,
          state: true,
          city: true,
          price: true,
          version: true,
          odometer: true,
          url: true,
          images: { select: { id: true, objectKey: true }, where: { order: 0 } },
        },
        take: 6,
        orderBy: { updatedAt: "desc" },
      }),
    { timings, type: "Similar cars loader" },
  );

  const chargingStations = await time(
    () =>
      prisma.location.findMany({
        where: {
          city: car.city,
          state: car.state,
        },
        select: {
          id: true,
          address: true,
          city: true,
          state: true,
          country: true,
          type: true,
          positives: true,
          negatives: true,
          latitude: true,
          longitude: true,
          totalReviews: true,
          icon_type: true,
          name: true,
          majorityNetworkId: true,
          url: true,
          averagePower: true,
          stations: {
            select: {
              id: true,
              locationId: true,
              outlets: {
                select: {
                  id: true,
                  stationId: true,
                  kilowatts: true,
                  power: true,
                  status: true,
                  connector: true,
                },
              },
            },
          },
        },
        take: 3,
        orderBy: { positives: "desc" }, // Order by positives descending
      }),
    { timings, type: "Charging stations loader" },
  );

  // Get historical price data for similar cars
  const historicalPrices = await time(
    () =>
      prisma.car.findMany({
        where: {
          AND: [
            { maker: car.maker },
            { model: car.model },
            // Only include version in the query if it exists
            ...(car.version ? [{ version: car.version }] : []),
            { fabricationYear: car.fabricationYear },
            { modelYear: car.modelYear },
            { isInactive: false },
            { isRemoved: false },
          ],
        },
        select: {
          price: true,
          createdAt: true,
          version: true,
        },
        orderBy: {
          createdAt: "asc",
        },
      }),
    { timings, type: "Historical prices loader" },
  );

  // Calculate monthly averages
  const monthlyAverages = historicalPrices.reduce((acc: any[], histCar) => {
    // Use a more descriptive name
    const month = new Date(histCar.createdAt).toISOString().slice(0, 7); // Get YYYY-MM
    const existingMonth = acc.find((m) => m.month === month);

    if (existingMonth) {
      existingMonth.prices.push(histCar.price || 0);
      existingMonth.average =
        existingMonth.prices.reduce((a: number, b: number) => a + b, 0) /
        existingMonth.prices.length;
    } else {
      acc.push({
        month,
        prices: [histCar.price || 0],
        average: histCar.price || 0,
      });
    }

    return acc;
  }, []);

  let finalHistoricalPrices = monthlyAverages;
  let priceHistoryIncludesVersion = Boolean(car.version);

  // If we have no data points and there was a version filter, try again without version
  if (monthlyAverages.length <= 1 && car.version) {
    const historicalPricesWithoutVersion = await time(
      () =>
        prisma.car.findMany({
          where: {
            AND: [
              { maker: car.maker },
              { model: car.model },
              { fabricationYear: car.fabricationYear },
              { modelYear: car.modelYear },
              { isInactive: false },
              { isRemoved: false },
            ],
          },
          select: {
            price: true,
            createdAt: true,
            version: true,
          },
          orderBy: {
            createdAt: "asc",
          },
        }),
      { timings, type: "Historical prices without version loader" },
    );

    // Recalculate monthly averages without version filter
    const monthlyAveragesWithoutVersion = historicalPricesWithoutVersion.reduce(
      (acc: any[], carWithoutVersion) => {
        // Renamed car variable
        const month = new Date(carWithoutVersion.createdAt)
          .toISOString()
          .slice(0, 7);
        const existingMonth = acc.find((m) => m.month === month);

        if (existingMonth) {
          existingMonth.prices.push(carWithoutVersion.price || 0);
          existingMonth.average =
            existingMonth.prices.reduce((a: number, b: number) => a + b, 0) /
            existingMonth.prices.length;
        } else {
          acc.push({
            month,
            prices: [carWithoutVersion.price || 0],
            average: carWithoutVersion.price || 0,
          });
        }

        return acc;
      },
      [],
    );

    // Use the broader data if it gives us more points
    if (monthlyAveragesWithoutVersion.length > monthlyAverages.length) {
      // return json( // Removed the early return here
      // 	{
      // 		car,
      // 		similarCars,
      // 		chargingStations,
      // 		historicalPrices: monthlyAveragesWithoutVersion,
      // 		priceHistoryIncludesVersion: false,
      // 	},
      // 	{
      // 		headers: {
      // 			'Cache-Control': 'private, max-age=3600',
      // 			'Server-Timing': timings.toString(),
      // 		},
      // 	},
      // )
      finalHistoricalPrices = monthlyAveragesWithoutVersion; // Assign to the final variable
      priceHistoryIncludesVersion = false;
    }
  }

  return json(
    {
      car,
      similarCars,
      chargingStations,
      historicalPrices: finalHistoricalPrices, // Use the final calculated data
      priceHistoryIncludesVersion,
    },
    {
      headers: {
        "Cache-Control": "private, max-age=3600",
        "Server-Timing": timings.toString(),
      },
    },
  );
}

export const handle: SEOHandle = {
  getSitemapEntries: serverOnly$(async (request) => {
    const cars = await prisma.car.findMany({
      where: {
        isRemoved: false,
      },
    });
    return cars.map((car) => {
      //@ts-ignore
      return {
        route: `/carros/${encodeURIComponent(car.url! + car.id)}`,
        priority: 0.7,
        changefreq: "monthly",
        lastmod: car.updatedAt.toISOString(),
      };
    });
  }),
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent") as string | null; // Handle null case

  // --- Handle Add to Garage ---
  if (intent === "add-to-garage") {
    const userId = await getUserId(request);

    if (!userId) {
      const redirectTo = formData.get("redirectTo");
      // Redirect to login if no user is logged in
      return redirect(`/login?redirectTo=${redirectTo}`);
    }

    const userCarId = formData.get("userCarId") as string;
    const subtypeId = formData.get("subtypeId") as string;
    const subtypeName = formData.get("subtypeName") as string | null;

    try {
      // Get the UserCar directly using userCarId
      const userCar = await prisma.userCar.findUnique({
        where: {
          id: parseInt(userCarId),
        },
      });

      if (!userCar) {
        return json(
          { response: "error", error: "Carro não encontrado" },
          { status: 404 },
        );
      }

      let modelRange;
      let modelBatteryCapacity;

      // If we have a subtypeId, get its range and batteryCapacity
      if (subtypeId) {
        const subtype = await prisma.subtype.findUnique({
          where: {
            id: parseInt(subtypeId),
          },
        });

        if (subtype) {
          modelRange = subtype.range;
          modelBatteryCapacity = subtype.batteryCapacity;
        }
      }

      // Fallback to userCar values if subtype values aren't available
      if (!modelRange) {
        modelRange = userCar.range || 200;
      }
      if (!modelBatteryCapacity) {
        modelBatteryCapacity = userCar.batteryCapacity;
      }

      // Check if this car is already in the user's garage
      const existingUserCar = await prisma.userUserCar.findFirst({
        where: {
          userId: userId,
          userCarId: parseInt(userCarId),
          subtypeName: subtypeName || "none",
        },
      });

      if (!existingUserCar) {
        // First, set all existing cars to not be the main car
        await prisma.userUserCar.updateMany({
          where: {
            userId: userId,
          },
          data: {
            isMain: false,
          },
        });

        // Create a new UserUserCar entry with isMain always set to true
        await prisma.userUserCar.create({
          data: {
            userId: userId,
            userCarId: parseInt(userCarId),
            subtypeName: subtypeName || "none",
            isMain: true, // Always set this car as the main car
            range: modelRange,
            batteryCapacity: modelBatteryCapacity,
          },
        });
      }

      // Redirect to trip planner
      return redirect("/mapa?planejar=viagem");
    } catch (error) {
      console.error("Error adding car to garage:", error);
      return json(
        {
          response: "error",
          error: "Failed to add car to garage",
          intent: "add-to-garage",
        }, // Added intent
        { status: 500 },
      );
    }
  }

  // --- Handle Email Contact ---
  if (intent === "contactSeller") {
    // Explicitly check for this intent
    checkHoneypot(formData); // Check honeypot first

    const carIdString = formData.get("carId") as string | null;
    const name = formData.get("name") as string | null;
    const email = formData.get("email") as string | null; // Sender's email
    const phone = formData.get("phone") as string | null;
    const message = formData.get("message") as string | null;
    const ownerEmail = formData.get("ownerEmail") as string | null; // Get owner's email from hidden input
    const carMakerModel = formData.get("carMakerModel") as string | null; // Get car details

    // Basic validation
    if (
      !carIdString ||
      !name ||
      !email ||
      !message ||
      !ownerEmail ||
      !carMakerModel
    ) {
      return json(
        {
          response: "error",
          error: "Faltam campos obrigatórios.",
          intent: "contactSeller",
        },
        { status: 400 },
      ); // Added intent
    }

    const carId = parseInt(carIdString, 10);
    if (isNaN(carId)) {
      return json(
        {
          response: "error",
          error: "ID do carro inválido.",
          intent: "contactSeller",
        },
        { status: 400 },
      ); // Added intent
    }

    // Send the email
    const response = await sendEmail({
      to: ownerEmail, // Send TO the owner's email
      subject: `Carregados - Interesse no seu ${carMakerModel || "veículo"}`, // Add car details to subject
      react: (
        <E.Html lang="pt-BR" dir="ltr">
          <E.Container>
            <E.Heading>
              <E.Text>Você tem um comprador interessado!</E.Text>
            </E.Heading>
            <E.Text>Veículo: {carMakerModel || "N/A"}</E.Text>
            <E.Hr />
            <E.Text>Nome: {name}</E.Text>
            <E.Text>Email para Resposta: {email}</E.Text>
            {phone && <E.Text>Telefone / Whatsapp: {phone}</E.Text>}
            <E.Section
              style={{
                marginTop: "20px",
                padding: "10px",
                border: "1px solid #eee",
              }}
            >
              <E.Text>Mensagem:</E.Text>
              <E.Text>{message}</E.Text>
            </E.Section>
            <E.Hr />
            <E.Text style={{ fontSize: "12px", color: "#666" }}>
              Enviado através do Carregados.com.br
            </E.Text>
          </E.Container>
        </E.Html>
      ),
    });

    if (response.status === "success") {
      // --- Increment Contact Count (Fire-and-forget) ---
      prisma.car
        .update({
          where: { id: carId },
          data: { contacts: { increment: 1 } },
        })
        .catch((error) => {
          console.error(
            `Failed to increment contact count for car ${carId} (email):`,
            error,
          );
          // Log error, but don't fail the user response
        });
      // --- End Increment Contact Count ---
      return json({ response: "success", intent: "contactSeller" }); // Added intent
    } else {
      console.error("Failed to send email:", response.error);
      return json(
        {
          response: "error",
          error: "Falha ao enviar o email.",
          intent: "contactSeller",
        },
        { status: 500 },
      ); // Added intent
    }
  }

  // Fallback for unknown intents or if intent is missing
  return json({ response: "error", error: "Ação inválida." }, { status: 400 }); // Generic error
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const car = data?.car ?? null;

  if (!car) {
    return [
      {
        title: "Carregados",
        description: "Carregados",
      },
    ];
  }

  const title = `${formatText(car.maker)} ${formatText(car.model)} ${formatText(
    car.version,
  )} à venda`
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const description = `Veja fotos, preço e informações sobre ${formatText(
    car.maker,
  )} ${formatText(car.model)} ${formatText(
    car.version,
  )} à venda. Encontre seu próximo carro no Carregados`
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const currentUrl = `https://carregados.com.br/carros/${car.url}${car.id}`;

  return [
    { title: title },
    {
      name: "description",
      content: description,
    },
    {
      itemProp: "name",
      content: title,
    },
    {
      itemProp: "description",
      content: description,
    },
    { itemProp: "image", content: "https://carregados.com.br/img/carregados-og.jpg" },
    { property: "og:url", content: currentUrl },
    { property: "og:type", content: "website" },
    {
      property: "og:title",
      content: title,
    },
    {
      property: "og:description",
      content: description,
    },
    {
      property: "og:image",
      content: "https://carregados.com.br/img/carregados-og.jpg",
    },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: title,
    },
    {
      name: "twitter:description",
      content: description,
    },
    {
      name: "twitter:image",
      content: "https://carregados.com.br/img/carregados-og.jpg",
    },
    {
      name: "twitter:image:alt",
      content: title,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: currentUrl,
    },
  ];
};

export default function Carro() {
  const {
    car,
    similarCars,
    chargingStations,
    historicalPrices,
    priceHistoryIncludesVersion,
  } = useLoaderData<typeof loader>();
  const actionData = useActionData<ActionData>(); // Use the specific type
  const fetcher = useFetcher<ActionData>(); // Typed fetcher for the main form
  const favoriteFetcher = useFetcher();
  const user = useOptionalUser();
  const { toast } = useToast();
  const [isContactVisible, setIsContactVisible] = useState(false);
  const [isPriceHistoryVisible, setIsPriceHistoryVisible] = useState(false);
  const [isFullscreenView, setIsFullscreenView] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mainImage, setMainImage] = useState(
    car.images?.[0]?.objectKey
      ? `${getCdnCarImgSrc(car.images[0].objectKey)}`
      : "",
  );
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [thumbnailsLoaded, setThumbnailsLoaded] = useState<
    Record<string, boolean>
  >({});

  // Get intent from action data, ensure actionData exists first
  const intent =
    actionData && "intent" in actionData ? actionData.intent : undefined;

  // Reset states when car ID changes (for navigation between cars)
  useEffect(() => {
    // Reset image loading states
    setMainImageLoaded(false);
    setThumbnailsLoaded({});

    // Ensure main image is set to the first image of the new car
    if (car.images?.[0]?.objectKey) {
      setMainImage(
        `${getCdnCarImgSrc(car.images[0].objectKey)}`,
      );
    } else {
      setMainImage("");
    }

    // Reset current image index
    setCurrentImageIndex(0);
  }, [car.id]);

  // Find current image index based on mainImage
  useEffect(() => {
    if (mainImage) {
      const index = car.images.findIndex((img) =>
        mainImage.includes(getCdnCarImgSrc(img.objectKey)),
      );
      if (index !== -1) {
        setCurrentImageIndex(index);
      }
    }
  }, [mainImage, car.images]);

  // Keyboard navigation for images
  const handleImageNavigation = useCallback(
    (direction: "next" | "prev") => {
      if (!car.images.length) return;

      let newIndex = currentImageIndex;
      if (direction === "next") {
        newIndex = (currentImageIndex + 1) % car.images.length;
      } else {
        newIndex =
          (currentImageIndex - 1 + car.images.length) % car.images.length;
      }

      const newImageUrl = `${getCdnCarImgSrc(car.images[newIndex].objectKey)}`;
      setMainImage(newImageUrl);
      setMainImageLoaded(false); // Reset loading state for new main image
    },
    [currentImageIndex, car.images],
  );

  // Swipe handlers for the main image
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleImageNavigation("next"),
    onSwipedRight: () => handleImageNavigation("prev"),
    preventScrollOnSwipe: true, // Prevent page scroll while swiping image
    trackMouse: true, // Allow swiping with mouse for desktop testing/use
  });

  // Add keyboard listener for arrow navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreenView) {
        if (e.key === "ArrowRight") {
          handleImageNavigation("next");
        } else if (e.key === "ArrowLeft") {
          handleImageNavigation("prev");
        } else if (e.key === "Escape") {
          setIsFullscreenView(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreenView, handleImageNavigation]);

  // Preload main image effect
  useEffect(() => {
    if (!mainImage) {
      setMainImageLoaded(true); // If no image, mark as loaded immediately
      return;
    }
    let isCancelled = false; // Add cleanup flag
    const img = new Image();
    img.src = mainImage;

    // Use img.decode() to wait for decoding
    img
      .decode()
      .then(() => {
        if (!isCancelled) {
          setMainImageLoaded(true);
        }
      })
      .catch((error) => {
        console.error('Image decoding failed:', error);
        // Still set loaded to true to remove spinner even on decode error
        if (!isCancelled) {
          setMainImageLoaded(true);
        }
      });

    // Cleanup function to prevent state update on unmount
    return () => {
      isCancelled = true;
    };
  }, [mainImage]);

  // Preload thumbnail images effect
  useEffect(() => {
    let isCancelled = false; // Add cleanup flag for the entire effect

    car.images.forEach((imgData) => {
      const thumbUrl = `${getCdnCarImgSrc(imgData.objectKey)}`;
      const image = new Image();
      image.src = thumbUrl;

      // Use image.decode() for thumbnails
      image
        .decode()
        .then(() => {
          if (!isCancelled) {
            // Check cleanup flag
            setThumbnailsLoaded((prev) => ({ ...prev, [imgData.id]: true }));
          }
        })
        .catch((error) => {
          console.error(`Thumbnail decoding failed for ${imgData.id}:`, error);
          // Still set loaded to true on error
          if (!isCancelled) {
            // Check cleanup flag
            setThumbnailsLoaded((prev) => ({ ...prev, [imgData.id]: true }));
          }
        });
    });

    // Cleanup function for the entire effect
    return () => {
      isCancelled = true;
    };
  }, [car.images]);

  const subscribedModels = user?.carSubscriptions.map((subscription) => {
    return subscription.model;
  });

  const favorites = user?.favorites.map((favorite) => {
    return favorite.carId;
  });

  const numberToLocale = (number: number) => {
    return new Intl.NumberFormat("pt-BR").format(number);
  };

  const loading = useNavigation();

  // Mapping from DB field names to user-friendly labels
  const featureMap: Record<
    keyof Omit<
      typeof car,
      | "vehicleAttributes"
      | "id"
      | "price"
      | "views"
      | "contacts"
      | "fabricationYear"
      | "modelYear"
      | "odometer"
      | "color"
      | "isIpvaPaid"
      | "isUniqueOwner"
      | "isWarrantyActive"
      | "isRevisionOk"
      | "isInactive"
      | "thirdPartyUrl"
      | "content"
      | "phone"
      | "email"
      | "createdAt"
      | "updatedAt"
      | "city"
      | "state"
      | "url"
      | "owner"
      | "images"
      | "userCar"
      | "subtype"
      | "maker"
      | "model"
      | "version"
      | "isBatteryOk"
      | "isOriginalChargerIncluded"
    >,
    string
  > = {
    hasFsd: "Full Self-Driving (FSD)",
    hasAutopilot: "Piloto Automático Básico",
    hasSunroof: "Teto Solar Panorâmico",
    hasPremiumInterior: "Interior Premium",
    hasPremiumSound: "Sistema de Som Premium",
    hasHeatedSeats: "Assentos Aquecidos",
    hasHud: "Head-up Display",
    hasChargingCables: "Carregador Incluso", // Adjusted name slightly
    has360Camera: "Camera 360°",
    hasVoiceAssistant: "Assistente de Voz",
    hasSmartEntry: "Smart Entry",
    hasElectricTrunk: "Porta Malas Elétrico",
    hasDualMotor: "Motor Duplo",
    hasAwd: "Tração nas Quatro Rodas (AWD)",
    hasHeatedSteeringWheel: "Volante Aquecido",
  };

  // Combine existing attributes with boolean features
  const allFeatures = [
    // Existing attributes
    ...(car.vehicleAttributes || []).map((attr) => attr.name),
    // Boolean features from the car model
    ...Object.entries(featureMap)
      .filter(([key]) => car[key as keyof typeof car]) // Check if the boolean field is true
      .map(([, label]) => label), // Get the user-friendly label
  ];

  return (
    <main className="flex-grow bg-white md:pb-20">
      {/* Navigation bar */}
      {/* ... breadcrumbs ... */}
      <div className="container py-4">
        <nav aria-label="Breadcrumb" className="mb-1 w-full">
          <ul className="flex items-center space-x-1 text-sm">
            <li className="flex items-center">
              <Link
                to="/carros"
                className="text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
              >
                Carros
              </Link>
              <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
              <Link
                to={`/carros?marca=${encodeURIComponent(car.maker || "")}`}
                className="capitalize text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
              >
                {formatText(car.maker)}
              </Link>
              <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
              <Link
                to={`/carros?marca=${encodeURIComponent(car.maker || "")}&modelo=${encodeURIComponent(car.model || "")}`}
                className="capitalize text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
              >
                {formatText(car.model)}
              </Link>
              <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground" />
            </li>
            {/* Add current car name (optional) */}
            <li className="truncate capitalize text-foreground">
              {formatText(car.version)}
            </li>
          </ul>
        </nav>
      </div>

      {/* Car details */}
      <div className="container">
        <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left column - Images and details */}
          <div className="space-y-8 lg:col-span-2">
            {/* Car gallery */}
            <div className="space-y-4">
              <div
                {...swipeHandlers} // Spread the swipe handlers here
                className="relative aspect-video cursor-zoom-in overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800"
                onClick={() => setIsFullscreenView(true)}
                role="button"
                tabIndex={0}
                aria-label="Ver imagem em tela cheia"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setIsFullscreenView(true);
                  }
                }}
              >
                {/* Loading spinner overlay */}
                {!mainImageLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 backdrop-blur-sm">
                    <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  </div>
                )}

                {/* Only render img tag if mainImage exists */}
                {mainImage && (
                  <img
                    src={mainImage}
                    // width={1920}
                    // height={1440}
                    alt={`${formatText(car.maker)} ${formatText(car.model)}`}
                    className={`h-full w-full object-cover transition-opacity duration-300 ${mainImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() => setMainImageLoaded(true)}
                    key={`main-image-${car.id}-${car.images[currentImageIndex]?.id ?? "no-image"}`} // Key update
                  />
                )}

                {/* Placeholder if no images */}
                {!mainImage && !mainImageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                    <Car
                      className="text-gray-400 dark:text-gray-600"
                      size={60}
                    />
                  </div>
                )}

                {/* Zoom icon overlay */}
                {mainImageLoaded &&
                  car.images.length > 0 && ( // Added condition
                    <>
                      <div className="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-xs text-white">
                        {currentImageIndex + 1} / {car.images.length}{" "}
                        {/* Dynamic counter */}
                      </div>
                      <div className="absolute right-2 top-2 rounded-full bg-black/40 p-1 text-white hover:bg-black/60">
                        <Maximize2 className="h-5 w-5" />
                      </div>
                    </>
                  )}
              </div>

              {/* Thumbnail carousel - Only show if there are multiple images */}
              {car.images.length > 1 && (
                <div className="mt-4">
                  <Carousel
                    opts={{
                      align: "start",
                      loop: false, // Consider setting loop to false if preferred
                      dragFree: true,
                      skipSnaps: false,
                      inViewThreshold: 0.6,
                      containScroll: "trimSnaps",
                      slidesToScroll: "auto",
                      breakpoints: {
                        "(min-width: 640px)": {
                          containScroll: "keepSnaps",
                        },
                        "(min-width: 1024px)": {
                          containScroll: "keepSnaps",
                          dragFree: false,
                        },
                      },
                    }}
                    className="w-full"
                  >
                    <CarouselContent className="-ml-2 md:-ml-4">
                      {car.images.map((img, index) => {
                        const imageUrl = `${getCdnCarImgSrc(img.objectKey)}`;
                        const thumbUrl = `${getCdnCarImgSrc(img.objectKey)}`; // Smaller thumb URL
                        const isThumbLoaded = thumbnailsLoaded[img.id];
                        const isSelected = currentImageIndex === index; // Check if selected

                        return (
                          <CarouselItem
                            key={index}
                            className="basis-1/3 pl-2 sm:basis-1/4 md:basis-1/5 md:pl-4 lg:basis-1/6"
                          >
                            <div className="p-1">
                              <button // Changed to button for better accessibility
                                onClick={() => {
                                  setMainImage(imageUrl);
                                  setCurrentImageIndex(index); // Update index immediately
                                  // Only reset loading state if the image is actually changing
                                  if (imageUrl !== mainImage) {
                                    setMainImageLoaded(false);
                                  }
                                }}
                                className={`block aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-gray-100 transition-all duration-300 dark:bg-gray-800 ${isSelected ? "opacity-100 ring-2 ring-primary" : "opacity-80 hover:opacity-100"}`} // Use isSelected
                                aria-label={`Ver imagem ${index + 1} de ${car.images.length}`}
                                aria-current={isSelected ? "true" : "false"} // Indicate current item
                              >
                                <AspectRatio
                                  ratio={16 / 9}
                                  className="relative"
                                >
                                  {/* Thumbnail skeleton */}
                                  {!isThumbLoaded && (
                                    <Skeleton className="absolute inset-0 flex items-center justify-center">
                                      <Car
                                        className="text-gray-300 dark:text-gray-600"
                                        size={20}
                                      />
                                    </Skeleton>
                                  )}

                                  <Img
                                    src={thumbUrl} // Use smaller thumb URL
                                    width={170}
                                    height={128}
                                    alt={`${formatText(car.maker)} ${formatText(car.model)} - Miniatura ${index + 1}`} // More descriptive alt
                                    className={`h-full w-full object-cover transition-opacity duration-300 ${isThumbLoaded ? 'opacity-100' : 'opacity-0'}`}
                                    onLoad={() => setThumbnailsLoaded(prev => ({ ...prev, [img.id]: true }))}
                                    key={`thumb-${car.id}-${img.id}`} // Add key to force image reload
                                  />
                                </AspectRatio>
                              </button>
                            </div>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                    {/* Conditionally render nav buttons if needed */}
                    {car.images.length > 5 && ( // Example condition
                      <div className="pointer-events-none absolute inset-y-2 left-7 right-7 flex items-center justify-between md:inset-y-5 md:left-6 md:right-6">
                        <CarouselPrevious
                          className="pointer-events-auto relative h-8 w-8 translate-x-0 rounded-full"
                          aria-label="Ver imagens anteriores"
                        />
                        <CarouselNext
                          className="pointer-events-auto relative h-8 w-8 translate-x-0 rounded-full"
                          aria-label="Ver próximas imagens"
                        />
                      </div>
                    )}
                  </Carousel>
                </div>
              )}
            </div>

            {/* Car title and price */}
            {/* ... unchanged title and price section ... */}
            <div>
              <h1 className="mb-2 text-3xl font-bold sm:text-4xl">
                {formatText(car.maker)} {formatText(car.model)}
              </h1>
              <p className="mb-4 text-foreground/70">
                {formatText(car.version)} {car.modelYear} |{" "}
                {car.color && formatText(car.color)} |{" "}
                {numberToLocale(car.odometer || 0)}km
              </p>
              <div className="mb-6 flex flex-wrap items-center gap-4">
                <div className="text-2xl font-bold text-primary sm:text-3xl">
                  {car.price?.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </div>

                {historicalPrices.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={() => setIsPriceHistoryVisible(true)}
                  >
                    <TrendingUp className="h-4 w-4" />
                    Histórico de preços
                  </Button>
                )}

                <div className="flex items-center text-foreground/70">
                  <MapPin className="mr-1 h-4 w-4" />
                  {formatText(car.city)}, {formatState(car.state)}
                </div>
                <div className="ml-auto flex gap-2">
                  {user ? (
                    <>
                      {favorites && favorites.includes(car.id) ? (
                        <favoriteFetcher.Form
                          method="post"
                          action="/resources/favorite"
                        >
                          <input type="hidden" name="carId" value={car.id} />
                          <input type="hidden" name="carUrl" value={car.url} />
                          <input type="hidden" name="userId" value={user?.id} />
                          <input type="hidden" name="intent" value="remove" />
                          <button
                            type="submit"
                            aria-label="Remover dos favoritos"
                            className="group rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-gray-200 focus:outline-none active:scale-90 dark:bg-gray-800 dark:hover:bg-gray-700"
                          >
                            <Heart className="h-5 w-5 fill-red-500 text-red-500 transition-all duration-300 group-hover:hidden" />
                            <HeartOff className="hidden h-5 w-5 text-red-500 transition-all duration-300 group-hover:block" />
                          </button>
                        </favoriteFetcher.Form>
                      ) : (
                        <favoriteFetcher.Form
                          method="post"
                          action="/resources/favorite"
                          className="relative"
                        >
                          <input type="hidden" name="carId" value={car.id} />
                          <input type="hidden" name="carUrl" value={car.url} />
                          <input type="hidden" name="userId" value={user?.id} />
                          <input type="hidden" name="intent" value="insert" />
                          <button
                            type="submit"
                            aria-label="Adicionar aos favoritos"
                            className="group rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-90 dark:bg-gray-800 dark:hover:bg-gray-700"
                          >
                            <Heart className="h-5 w-5 transition-all duration-300 group-hover:scale-110 group-hover:text-red-500 group-active:animate-ping" />
                          </button>
                        </favoriteFetcher.Form>
                      )}
                    </>
                  ) : (
                    <Link
                      to={`/login?redirectTo=/carros/${car.url}${car.id}`}
                      className="relative"
                    >
                      {" "}
                      {/* Add redirectTo */}
                      <button
                        aria-label="Adicionar aos favoritos (requer login)"
                        className="group rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-90 dark:bg-gray-800 dark:hover:bg-gray-700"
                      >
                        <Heart className="h-5 w-5 transition-all duration-300 group-hover:scale-110 group-hover:text-red-500" />
                      </button>
                    </Link>
                  )}
                  <button
                    aria-label="Compartilhar anúncio"
                    className="group rounded-full bg-gray-100 p-2 transition-all duration-300 hover:bg-gray-200 focus:outline-none active:scale-90 dark:bg-gray-800 dark:hover:bg-gray-700"
                    onClick={() => {
                      const shareData = {
                        title: `${formatText(car.maker)} ${formatText(car.model)} ${formatText(car.version)}`,
                        text: `Confira este ${formatText(car.maker)} ${formatText(car.model)} ${formatText(car.version)} ano ${car.modelYear} no Carregados`,
                        url: window.location.href,
                      };

                      // Use Web Share API if available
                      if (navigator.share && navigator.canShare(shareData)) {
                        navigator
                          .share(shareData)
                          .then(() => {
                            toast({
                              title: "Compartilhado",
                              description: "Link compartilhado com sucesso",
                            });
                          })
                          .catch((error) => {
                            // Avoid logging AbortError if user cancels share dialog
                            if (
                              error instanceof Error &&
                              error.name !== "AbortError"
                            ) {
                              console.error("Erro ao compartilhar:", error);
                              toast({
                                variant: "destructive",
                                title: "Erro",
                                description:
                                  "Não foi possível compartilhar o link.",
                              });
                            }
                          });
                      } else {
                        // Fallback to clipboard
                        navigator.clipboard
                          .writeText(window.location.href)
                          .then(() => {
                            toast({
                              title: "Link copiado",
                              description:
                                "Link copiado para a área de transferência",
                            });
                          })
                          .catch((error) => {
                            console.error("Erro ao copiar:", error);
                            toast({
                              variant: "destructive",
                              title: "Erro",
                              description: "Não foi possível copiar o link",
                            });
                          });
                      }
                    }}
                  >
                    <Share2 className="h-5 w-5 transition-all duration-300 group-hover:scale-110 group-active:animate-ping" />
                  </button>
                </div>
              </div>
              {/* "Simular Viagem" button */}
              {(car.userCar?.id || car.subtype?.id) && ( // <-- Add this conditional check
                <Form method="post" className="mb-6">
                  <input type="hidden" name="intent" value="add-to-garage" />
                  {/* Use optional chaining and nullish coalescing for safety */}
                  <input
                    type="hidden"
                    name="userCarId"
                    value={car.userCar?.id ?? ""} // Provide empty string as fallback
                  />
                  <input
                    type="hidden"
                    name="subtypeId"
                    value={car.subtype?.id ?? ""} // Provide empty string as fallback
                  />
                  {car.subtype && (
                    <input
                      type="hidden"
                      name="subtypeName"
                      value={car.subtype.name}
                    />
                  )}
                  <input
                    type="hidden"
                    name="redirectTo"
                    value={`/carros/${car.url}${car.id}`}
                  />
                  <Button
                    type="submit"
                    className="group w-full bg-primary py-6 text-lg transition-all duration-300 hover:bg-primary/90"
                  >
                    <Route className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                    Simular Viagem com este Veículo
                  </Button>
                </Form>
              )}{" "}
              {/* <-- Close the conditional check */}
              {/* Key specs */}
              {/* ... unchanged key specs ... */}
              <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-4 text-center dark:bg-gray-900">
                  <CalendarDays className="mb-2 h-5 w-5 text-primary" />
                  <p className="text-sm text-foreground/70">Ano</p>
                  <p className="font-bold">
                    {car.fabricationYear}/{car.modelYear}
                  </p>{" "}
                  {/* Display both years */}
                </div>
                <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-4 text-center dark:bg-gray-900">
                  <Clock className="mb-2 h-5 w-5 text-primary" />
                  <p className="text-sm text-foreground/70">Quilometragem</p>
                  <p className="font-bold">
                    {numberToLocale(car.odometer || 0)} km
                  </p>
                </div>
                <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-4 text-center dark:bg-gray-900">
                  <BatteryFull className="mb-2 h-5 w-5 text-primary" />
                  <p className="text-sm text-foreground/70">Bateria</p>
                  <p className="font-bold">
                    {car.subtype?.batteryCapacity ||
                      car.userCar?.batteryCapacity ||
                      "N/D"}{" "}
                    {/* Use N/D for consistency */}
                    kWh
                  </p>
                </div>
                <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 p-4 text-center dark:bg-gray-900">
                  <Compass className="mb-2 h-5 w-5 text-primary" />
                  <p className="text-sm text-foreground/70">Autonomia</p>
                  <p className="font-bold">
                    {car.subtype?.range || car.userCar?.range
                      ? `${Math.round((car.subtype?.range || car.userCar?.range || 0) / 1000)} km` // Round to nearest km
                      : "N/D"}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs for car details */}
            {/* ... unchanged tabs ... */}
            <Tabs defaultValue="description">
              <TabsList
                className={cn(
                  "mb-4 grid h-auto w-full bg-gray-100 dark:bg-gray-800",
                  allFeatures.length > 0 ? "grid-cols-3" : "grid-cols-2", // Dynamically set grid columns
                )}
              >
                <TabsTrigger
                  value="description"
                  className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  Descrição
                </TabsTrigger>
                <TabsTrigger
                  value="specs"
                  className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  Especificações
                </TabsTrigger>
                {allFeatures.length > 0 && ( // Conditionally render features tab trigger
                  <TabsTrigger
                    value="features"
                    className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-md"
                  >
                    Equipamentos
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent
                value="description"
                className="mt-4 md:min-h-[25rem] rounded-xl bg-gray-50 p-6 dark:bg-gray-900" // Added min-h
              >
                <article className="prose prose-sm w-full max-w-none dark:prose-invert sm:prose-base prose-a:text-blue-600 prose-strong:text-foreground">
                  {" "}
                  {/* Adjusted prose size and dark mode */}
                  {car.content ? (
                    <Markdown>{car.content.replaceAll("<br>", "\n")}</Markdown>
                  ) : (
                    <p className="text-muted-foreground">
                      Nenhuma descrição fornecida.
                    </p>
                  )}
                </article>
              </TabsContent>

              <TabsContent value="specs" className="mt-4 md:min-h-[25rem]">
                {" "}
                {/* Added min-h */} {/* Add mt-4 */}
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Marca</TableCell>
                      <TableCell>{formatText(car.maker)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Modelo</TableCell>
                      <TableCell>{formatText(car.model)}</TableCell>
                    </TableRow>
                    {car.version && ( // Only show if version exists
                      <TableRow>
                        <TableCell className="font-medium">Versão</TableCell>
                        <TableCell>{formatText(car.version)}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell className="font-medium">Ano</TableCell>
                      <TableCell>
                        {car.fabricationYear}/{car.modelYear}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">
                        Quilometragem
                      </TableCell>
                      <TableCell>
                        {numberToLocale(car.odometer || 0)} km
                      </TableCell>
                    </TableRow>
                    {car.color && ( // Only show if color exists
                      <TableRow>
                        <TableCell className="font-medium">Cor</TableCell>
                        <TableCell>{formatText(car.color)}</TableCell>
                      </TableRow>
                    )}
                    {(car.subtype?.batteryCapacity ||
                      car.userCar?.batteryCapacity) && ( // Only show if capacity exists
                      <TableRow>
                        <TableCell className="font-medium">Bateria</TableCell>
                        <TableCell>
                          {car.subtype?.batteryCapacity ||
                            car.userCar?.batteryCapacity}{" "}
                          kWh
                        </TableCell>
                      </TableRow>
                    )}
                    {(car.subtype?.range || car.userCar?.range) && ( // Only show if range exists
                      <TableRow>
                        <TableCell className="font-medium">
                          Autonomia (WLTP)
                        </TableCell>{" "}
                        {/* Specify cycle if known */}
                        <TableCell>
                          {Math.round(
                            (car.subtype?.range || car.userCar?.range || 0) /
                              1000,
                          )}{" "}
                          km
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              {allFeatures.length > 0 && ( // Conditionally render features content
                <TabsContent value="features" className="mt-4 md:min-h-[25rem]">
                  {" "}
                  {/* Add mt-4 */}
                  {/* Content moved inside conditional block */}
                  <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
                    {allFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="mr-2 h-2 w-2 rounded-full bg-primary"></div>
                        <span className="capitalize">{feature}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>

            {/* Car condition - only shown if at least one condition is true */}
            {/* ... unchanged condition section ... */}
            {(car.isIpvaPaid ||
              car.isWarrantyActive ||
              car.isUniqueOwner ||
              car.isRevisionOk ||
              car.isBatteryOk || // Added for battery condition
              car.isOriginalChargerIncluded) && ( // Added for charger condition
              <div className="mt-8 space-y-4">
                <h3 className="text-xl font-bold">Estado do veículo</h3>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {car.isIpvaPaid && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <Receipt className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm text-foreground/70">IPVA</p>
                          <p className="font-bold">IPVA pago</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {car.isWarrantyActive && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <ShieldCheck className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm text-foreground/70">Garantia</p>
                          <p className="font-bold">Garantia de fábrica ativa</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {car.isUniqueOwner && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <UserCheck className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm text-foreground/70">
                            Documentação
                          </p>
                          <p className="font-bold">Único dono</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {car.isRevisionOk && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <Wrench className="mr-3 h-5 w-5 text-primary" />
                        <div>
                          <p className="text-sm text-foreground/70">Revisões</p>
                          <p className="font-bold">
                            Revisões na Concessionária
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* --- New Condition: Battery --- */}
                  {car.isBatteryOk && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <BatteryCharging className="mr-3 h-5 w-5 text-primary" />{" "}
                        {/* Use Lucide icon */}
                        <div>
                          <p className="text-sm text-foreground/70">Bateria</p>
                          <p className="font-bold">Bateria em bom estado</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* --- New Condition: Charger --- */}
                  {car.isOriginalChargerIncluded && (
                    <Card>
                      <CardContent className="flex items-center p-4">
                        <PlugZap className="mr-3 h-5 w-5 text-primary" />{" "}
                        {/* Use Lucide icon */}
                        <div>
                          <p className="text-sm text-foreground/70">
                            Carregador
                          </p>
                          <p className="font-bold">
                            Carregador original incluso
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {/* --- End New Conditions --- */}
                </div>
              </div>
            )}
          </div>

          {/* Right column - Seller info and contact */}
          <div>
            <div className="sticky top-8 space-y-6">
              {/* Seller card */}
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="border-b p-6">
                    <div className="mb-4 flex items-center gap-4">
                      {car.owner?.image?.objectKey ? (
                        <Img
                          src={`${getCdnUserImgSrc(car.owner.image.objectKey)}`}
                          width={60}
                          height={60}
                          alt={`Foto de perfil de ${car.owner?.username || "Vendedor"}`} // More descriptive alt
                          className="h-14 w-14 rounded-full border border-gray-200 object-cover shadow-sm dark:border-gray-600"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-gray-200 shadow-sm dark:border-gray-600 dark:bg-gray-700">
                          <User2 className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-bold">
                          {car.owner?.username || "Vendedor Particular"}{" "}
                          {/* Be more specific */}
                        </h3>

                        <div className="flex items-center">
                          <Award className="mr-1 h-4 w-4 text-yellow-500" />
                          <span className="text-sm">Membro verificado</span>
                        </div>
                      </div>
                    </div>

                    {!car.isInactive ? (
                      <>
                        {!car.thirdPartyUrl ? (
                          <>
                            {
                              car.phone && // Only show WhatsApp if phone exists
                                (() => {
                                  // Construct the original WhatsApp URL
                                  const whatsappUrl = `https://api.whatsapp.com/send?phone=55${car.phone.replace(/\D/g, "")}&text=${encodeURIComponent(`Olá! Vi seu anúncio de ${formatText(car.maker)} ${formatText(car.model)} no carregados.com.br e estou interessado.`)}`;
                                  // Construct the tracking URL
                                  const trackingUrl = `/resources/track-contact?type=whatsapp&carId=${car.id}&redirectTo=${encodeURIComponent(whatsappUrl)}`;

                                  return (
                                    <a
                                      className="mb-3 block w-full"
                                      target="_blank" // Keep target blank
                                      href={trackingUrl} // Use the tracking URL
                                      rel="noreferrer noopener" // Use noopener for security with target blank
                                    >
                                      <Button className="w-full gap-2 bg-[#25D366] px-4 py-3 font-medium text-white hover:bg-[#128C7E]">
                                        {" "}
                                        {/* Adjusted hover color */}
                                        {/* WhatsApp Icon SVG */}
                                        <svg
                                          fill="#ffffff"
                                          height="20px"
                                          width="20px"
                                          version="1.1"
                                          id="Layer_1"
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 308 308"
                                          stroke="#ffffff"
                                        >
                                          <g
                                            id="SVGRepo_bgCarrier"
                                            strokeWidth="0"
                                          ></g>
                                          <g
                                            id="SVGRepo_tracerCarrier"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></g>
                                          <g id="SVGRepo_iconCarrier">
                                            {" "}
                                            <g id="XMLID_468_">
                                              {" "}
                                              <path
                                                id="XMLID_469_"
                                                d="M227.904,176.981c-0.6-0.288-23.054-11.345-27.044-12.781c-1.629-0.585-3.374-1.156-5.23-1.156 c-3.032,0-5.579,1.511-7.563,4.479c-2.243,3.334-9.033,11.271-11.131,13.642c-0.274,0.313-0.648,0.687-0.872,0.687 c-0.201,0-3.676-1.431-4.728-1.888c-24.087-10.463-42.37-35.624-44.877-39.867c-0.358-0.61-0.373-0.887-0.376-0.887 c0.088-0.323,0.898-1.135,1.316-1.554c1.223-1.21,2.548-2.805,3.83-4.348c0.607-0.731,1.215-1.463,1.812-2.153 c1.86-2.164,2.688-3.844,3.648-5.79l0.503-1.011c2.344-4.657,0.342-8.587-0.305-9.856c-0.531-1.062-10.012-23.944-11.02-26.348 c-2.424-5.801-5.627-8.502-10.078-8.502c-0.413,0,0,0-1.732,0.073c-2.109,0.089-13.594,1.601-18.672,4.802 c-5.385,3.395-14.495,14.217-14.495,33.249c0,17.129,10.87,33.302,15.537,39.453c0.116,0.155,0.329,0.47,0.638,0.922 c17.873,26.102,40.154,45.446,62.741,54.469c21.745,8.686,32.042,9.69,37.896,9.69c0.001,0,0.001,0,0.001,0 c2.46,0,4.429-0.193,6.166-0.364l1.102-0.105c7.512-0.666,24.02-9.22,27.775-19.655c2.958-8.219,3.738-17.199,1.77-20.458 C233.168,179.508,230.845,178.393,227.904,176.981z"
                                              ></path>{" "}
                                              <path
                                                id="XMLID_470_"
                                                d="M156.734,0C73.318,0,5.454,67.354,5.454,150.143c0,26.777,7.166,52.988,20.741,75.928L0.212,302.716 c-0.484,1.429-0.124,3.009,0.933,4.085C1.908,307.58,2.943,308,4,308c0.405,0,0.813-0.061,1.211-0.188l79.92-25.396 c21.87,11.685,46.588,17.853,71.604,17.853C240.143,300.27,308,232.923,308,150.143C308,67.354,240.143,0,156.734,0z M156.734,268.994c-23.539,0-46.338-6.797-65.936-19.657c-0.659-0.433-1.424-0.655-2.194-0.655c-0.407,0-0.815,0.062-1.212,0.188 l-40.035,12.726l12.924-38.129c0.418-1.234,0.209-2.595-0.561-3.647c-14.924-20.392-22.813-44.485-22.813-69.677 c0-65.543,53.754-118.867,119.826-118.867c66.064,0,119.812,53.324,119.812,118.867 C276.546,215.678,222.799,268.994,156.734,268.994z"
                                              ></path>{" "}
                                            </g>{" "}
                                          </g>
                                        </svg>
                                        WhatsApp
                                      </Button>
                                    </a>
                                  );
                                })() // IIFE to keep scope clean
                            }

                            <button
                              onClick={() => setIsContactVisible(true)}
                              className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90"
                            >
                              {car.phone
                                ? "Enviar mensagem por email"
                                : "Contatar vendedor"}
                            </button>
                          </>
                        ) : (
                          // External Link logic - remains the same
                          <Link
                            rel="nofollow noopener"
                            target="_blank"
                            // Track external clicks via a similar resource route if needed
                            to={`/redirect/externo?carid=${car.id}`} // Or track here
                          >
                            <Button className="w-full gap-2 bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90">
                              <ExternalLink className="h-5 w-5" />
                              Contatar vendedor
                            </Button>
                          </Link>
                        )}
                      </>
                    ) : (
                      // Inactive listing logic - remains the same
                      <div className="flex w-full flex-col items-center justify-center space-y-2">
                        <h4 className="text-center font-medium">
                          Este anúncio está encerrado
                        </h4>
                        <Link
                          prefetch="intent"
                          to={`/carros?marca=${car.maker}&modelo=${car.model}`}
                          className="text-sm"
                        >
                          <Button variant="link" className="h-auto gap-1 p-0">
                            {`Ver outros anúncios de`}
                            <span className="capitalize">{`${" "}${formatText(
                              car.maker,
                            )} ${formatText(car.model)}`}</span>
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>{" "}
                  {/* End border-b p-6 */}
                  {/* Charging Information section */}
                  {/* ... unchanged charging info ... */}
                  {(car.subtype?.batteryCapacity ||
                    car.userCar?.batteryCapacity ||
                    car.subtype?.range ||
                    car.userCar?.range ||
                    car.subtype?.compatibleOutlets?.length ||
                    car.userCar?.compatibleOutlets?.length) && (
                    <div className="rounded-b-xl bg-gradient-to-br from-gray-50 to-gray-100 p-6 dark:from-gray-900 dark:to-gray-800">
                      <div className="mb-4 flex items-center justify-center font-semibold">
                        <Zap className="mr-2 h-5 w-5 text-primary" />
                        <h4>Informações de Carregamento</h4>
                      </div>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          {(car.subtype?.batteryCapacity ||
                            car.userCar?.batteryCapacity) && (
                            <div className="flex flex-col items-center justify-center rounded-lg bg-white/80 p-3 shadow-sm transition-transform hover:scale-[1.02] dark:bg-gray-800/80">
                              <BatteryFull className="mb-1 h-5 w-5 text-primary" />
                              <span className="text-xs text-foreground/70">
                                Capacidade
                              </span>
                              <span className="font-semibold">
                                {car.subtype?.batteryCapacity ||
                                  car.userCar?.batteryCapacity}{" "}
                                kWh
                              </span>
                            </div>
                          )}
                          {(car.subtype?.range || car.userCar?.range) && (
                            <div className="flex flex-col items-center justify-center rounded-lg bg-white/80 p-3 shadow-sm transition-transform hover:scale-[1.02] dark:bg-gray-800/80">
                              <Compass className="mb-1 h-5 w-5 text-primary" />
                              <span className="text-xs text-foreground/70">
                                Autonomia
                              </span>
                              <span className="font-semibold">
                                {Math.round(
                                  (car.subtype?.range ||
                                    car.userCar?.range ||
                                    0) / 1000,
                                )}{" "}
                                km
                              </span>
                            </div>
                          )}
                        </div>

                        {(car.subtype?.compatibleOutlets?.length ||
                          car.userCar?.compatibleOutlets?.length) && (
                          <div className="mt-4">
                            <h4 className="mb-2 flex items-center justify-center gap-2 text-center font-semibold">
                              <Unplug className="h-5 w-5 text-primary" />
                              Conectores compatíveis
                            </h4>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              {(() => {
                                // Determine the source of compatible outlets
                                const compatibleOutlets = car.subtype
                                  ?.compatibleOutlets?.length
                                  ? car.subtype.compatibleOutlets
                                  : (car.userCar?.compatibleOutlets ?? []);

                                // Map outlets to connector info and filter out nulls/unnamed
                                const allConnectorInfo = compatibleOutlets
                                  .map((outlet: { connector: number }) =>
                                    getConnectorInfo(outlet.connector),
                                  )
                                  .filter(
                                    (
                                      info,
                                    ): info is NonNullable<
                                      ReturnType<typeof getConnectorInfo>
                                    > =>
                                      // Ensure info is not null and name is a non-empty string
                                      info !== null &&
                                      typeof info.name === "string" &&
                                      info.name.trim() !== "",
                                  );

                                // Filter for unique connector names
                                const uniqueConnectorNames = new Set<string>();
                                const uniqueConnectorInfo =
                                  allConnectorInfo.filter((info) => {
                                    if (!uniqueConnectorNames.has(info.name)) {
                                      uniqueConnectorNames.add(info.name);
                                      return true;
                                    }
                                    return false;
                                  });

                                // Render the unique connectors
                                return uniqueConnectorInfo.map(
                                  (connectorInfo, index) => (
                                    <div
                                      key={index} // Use index or a unique identifier from connectorInfo if available
                                      className="flex items-center rounded-lg bg-white/80 px-3 py-2 shadow-sm transition-all hover:bg-white hover:shadow dark:bg-gray-800/80 dark:hover:bg-gray-800"
                                    >
                                      {connectorInfo.icon && (
                                        <Icon
                                          name={connectorInfo.icon as any}
                                          className="mr-2 h-6 w-6 shrink-0 text-primary"
                                        />
                                      )}
                                      <span className="truncate text-sm">
                                        {connectorInfo.name}
                                      </span>
                                    </div>
                                  ),
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Similar cars card */}
              {/* ... unchanged similar cars ... */}
              {similarCars.length > 0 && (
                <Card className="overflow-hidden">
                  <CardContent className="p-6">
                    <h3 className="mb-4 font-bold">Carros similares</h3>

                    <div className="space-y-4">
                      {similarCars.slice(0, 3).map((similarCar) => (
                        <Link
                          to={`/carros/${similarCar.url}${similarCar.id}`}
                          key={similarCar.id}
                          className="group flex gap-3"
                          prefetch="intent"
                        >
                          <div className="h-16 w-20 flex-shrink-0 overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
                            {similarCar.images[0] ? (
                              <Img
                                src={`${getCdnCarImgSrc(similarCar.images[0].objectKey)}`}
                                width={96}
                                height={64}
                                alt={`${formatText(similarCar.maker)} ${formatText(similarCar.model)}`}
                                className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                key={`similar-${similarCar.id}-${similarCar.images[0].id}`}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <Car className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h4 className="line-clamp-1 font-medium transition-colors group-hover:text-primary">
                              {" "}
                              {/* Added line-clamp */}
                              {formatText(similarCar.maker)}{" "}
                              {formatText(similarCar.model)}
                            </h4>
                            <p className="text-sm text-foreground/70">
                              {similarCar.price?.toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })}
                            </p>
                            <p className="text-xs">
                              {similarCar.modelYear} ·{" "}
                              {numberToLocale(similarCar.odometer || 0)} km
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {similarCars.length > 3 && (
                      <Link
                        to={`/carros?marca=${encodeURIComponent(car.maker || "")}&modelo=${encodeURIComponent(car.model || "")}`}
                        prefetch="intent"
                        className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
                      >
                        Ver mais carros similares
                        <ChevronRight className="ml-1 h-4 w-4" />{" "}
                        {/* Changed icon */}
                      </Link>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact form modal - only shown for non-third-party listings */}
      {isContactVisible && !car.thirdPartyUrl && !car.isInactive && (
        <Dialog open={isContactVisible} onOpenChange={setIsContactVisible}>
          {" "}
          {/* Using Dialog */}
          <DialogContent className="sm:max-w-md">
            {" "}
            {/* Adjusted size */}
            <DialogHeader>
              <DialogTitle>Contatar vendedor</DialogTitle>
              <DialogDescription>
                Envie uma mensagem sobre o {formatText(car.maker)}{" "}
                {formatText(car.model)}.
              </DialogDescription>
            </DialogHeader>
            <div className="p-0 pt-4">
              {" "}
              {/* Removed outer p-6 */}
              {/* Car interest display */}
              <div className="mb-6 px-6">
                {" "}
                {/* Added px-6 here */}
                {/* <h3 className="mb-2 font-medium">Seu interesse</h3> */}
                <div className="flex items-center gap-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  {car.images[0]?.id && ( // Check if image exists
                    <Img
                      src={`${getCdnCarImgSrc(car.images[0].objectKey)}`}
                      width={100}
                      height={75}
                      alt={`${formatText(car.maker)} ${formatText(car.model)}`}
                      className="h-16 w-20 rounded object-cover"
                    />
                  )}
                  <div>
                    <h4 className="font-medium">
                      {formatText(car.maker)} {formatText(car.model)}{" "}
                      {formatText(car.version)}
                    </h4>
                    <p className="text-foreground/70">
                      {car.price?.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                  </div>
                </div>
              </div>
              {/* End car interest display */}
              {/* Check if action was successful BEFORE rendering the form */}
              {/* Check based on the defined ActionData type */}
              {fetcher.state === "idle" && // Check if the fetcher is done loading
              fetcher.data?.response === "success" &&
              fetcher.data?.intent === "contactSeller" ? ( // Check fetcher.data for success and intent
                <div className="px-6 py-8 text-center">
                  {" "}
                  {/* Added px-6 */}
                  <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
                  <p className="text-lg font-semibold">
                    Mensagem enviada com sucesso!
                  </p>
                  <p className="text-muted-foreground">
                    O vendedor entrará em contato em breve.
                  </p>
                  <Button
                    onClick={() => setIsContactVisible(false)}
                    variant="outline"
                    className="mt-6"
                  >
                    Fechar
                  </Button>
                </div>
              ) : (
                <fetcher.Form method="post" className="space-y-4 px-6 pb-6">
                  {" "}
                  {/* Use fetcher.Form and add padding */}
                  <HoneypotInputs />
                  {/* --- Add Hidden Inputs --- */}
                  <input type="hidden" name="intent" value="contactSeller" />
                  <input type="hidden" name="carId" value={car.id} />
                  {/* Pass owner's email to the action */}
                  <input
                    type="hidden"
                    name="ownerEmail"
                    value={car.owner?.email || ""}
                  />
                  {/* Pass car details for email subject/body */}
                  <input
                    type="hidden"
                    name="carMakerModel"
                    value={`${formatText(car.maker)} ${formatText(car.model)}`}
                  />
                  {/* --- End Hidden Inputs --- */}
                  <div>
                    <Label
                      htmlFor="contact-name"
                      className="mb-1 block text-sm font-medium"
                    >
                      {" "}
                      {/* Unique ID */}
                      Seu nome
                    </Label>
                    <Input
                      type="text"
                      id="contact-name" // Unique ID
                      name="name"
                      className="w-full rounded-lg border p-3"
                      placeholder="Nome completo"
                      required
                      defaultValue={user?.name ?? ""} // Pre-fill if user is logged in
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contact-email"
                      className="mb-1 block text-sm font-medium"
                    >
                      {" "}
                      {/* Unique ID */}
                      Seu email
                    </Label>
                    <Input
                      type="email"
                      id="contact-email" // Unique ID
                      name="email"
                      className="w-full rounded-lg border p-3"
                      placeholder="exemplo@email.com"
                      required
                      // Remove defaultValue for user.email
                      // defaultValue={user?.email ?? ''}
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contact-phone"
                      className="mb-1 block text-sm font-medium"
                    >
                      {" "}
                      {/* Unique ID */}
                      Seu telefone{" "}
                      {car.phone ? (
                        <span className="ml-1 text-muted-foreground">
                          (opcional)
                        </span>
                      ) : (
                        ""
                      )}
                    </Label>
                    <Input
                      type="tel"
                      id="contact-phone" // Unique ID
                      name="phone"
                      className="w-full rounded-lg border p-3"
                      placeholder="(11) 99999-9999"
                      required={!car.phone} // Only required if seller *didn't* provide phone for WhatsApp
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="contact-message"
                      className="mb-1 block text-sm font-medium"
                    >
                      {" "}
                      {/* Unique ID */}
                      Mensagem
                    </Label>
                    <Textarea
                      id="contact-message" // Unique ID
                      name="message"
                      className="min-h-[100px] w-full rounded-lg border p-3"
                      placeholder={`Olá, tenho interesse neste ${formatText(car.maker)} ${formatText(car.model)} e gostaria de mais informações...`}
                      required
                      defaultValue={`Olá, tenho interesse neste ${formatText(car.maker)} ${formatText(car.model)} anunciado no Carregados e gostaria de mais informações.`} // Default message
                    />
                  </div>
                  {/* Display server-side error message, check based on ActionData type */}
                  {/* Add explicit check for 'intent' property existence */}
                  {fetcher.state === "idle" && // Check if fetcher is done
                  fetcher.data?.response === "error" && // Check for error in fetcher.data
                  // Explicitly check if intent exists before accessing it
                  "intent" in fetcher.data &&
                  fetcher.data.intent === "contactSeller" ? (
                    <p className="text-sm text-red-600 dark:text-red-500">
                      {fetcher.data.error || // Use error from fetcher.data
                        "Ocorreu um erro ao enviar a mensagem."}
                    </p>
                  ) : null}
                  <div className="pt-2">
                    {/* Button state based on fetcher loading specific to this form */}
                    <Button
                      type="submit"
                      className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90"
                      // Disable based on the fetcher's state when submitting *this* form
                      disabled={
                        fetcher.state !== "idle" &&
                        fetcher.formData?.get("intent") === "contactSeller"
                      }
                    >
                      {fetcher.state !== "idle" &&
                      fetcher.formData?.get("intent") === "contactSeller"
                        ? "Enviando..."
                        : "Enviar mensagem"}
                    </Button>
                  </div>
                </fetcher.Form>
              )}{" "}
              {/* End conditional rendering for form/success message */}
            </div>{" "}
            {/* End p-0 pt-4 */}
          </DialogContent>
        </Dialog>
      )}

      {/* Price History Modal */}
      {/* ... unchanged price history modal ... */}
      <Dialog
        open={isPriceHistoryVisible}
        onOpenChange={setIsPriceHistoryVisible}
      >
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Histórico de Preços</DialogTitle>
            <DialogDescription>
              {priceHistoryIncludesVersion
                ? `Preços históricos para ${formatText(car.maker)} ${formatText(car.model)} ${formatText(car.version)} ${car.fabricationYear}/${car.modelYear}`
                : `Preços históricos para ${formatText(car.maker)} ${formatText(car.model)} ${car.fabricationYear}/${car.modelYear}`}
              {` (média mensal)`} {/* Added clarification */}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[400px] w-full pt-4">
            {" "}
            {/* Added pt-4 */}
            {historicalPrices.length > 1 ? (
              <PriceHistoryChart data={historicalPrices} />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <p className="text-center text-muted-foreground">
                  Não há dados históricos suficientes para este veículo.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Modal */}
      {/* ... unchanged fullscreen modal ... */}
      {isFullscreenView &&
        car.images.length > 0 && ( // Added condition
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm">
            {" "}
            {/* Added backdrop */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="relative flex h-full w-full flex-col items-center justify-center">
                {" "}
                {/* Changed to flex-col */}
                {/* Close button */}
                <button
                  onClick={() => setIsFullscreenView(false)}
                  className="absolute right-4 top-4 z-20 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70" // Increased z-index
                  aria-label="Fechar visualização em tela cheia"
                >
                  <X className="h-6 w-6" />
                </button>
                {/* Image counter */}
                <div className="absolute left-4 top-4 z-20 rounded bg-black/50 px-3 py-2 text-sm text-white">
                  {" "}
                  {/* Increased z-index */}
                  {currentImageIndex + 1} / {car.images.length}
                </div>
                {/* Main fullscreen image container */}
                <div
                  {...swipeHandlers} // Apply swipe handlers here too
                  className="relative mb-[100px] flex w-full flex-grow items-center justify-center overflow-hidden"
                >
                  {/* Image Loading Spinner */}
                  {!mainImageLoaded && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
                      <Loader2 className="h-12 w-12 animate-spin text-white" />
                    </div>
                  )}
                  <img
                  src={mainImage}
                    // width={1920}
                    // height={1440}  
                    alt={`${formatText(car.maker)} ${formatText(car.model)} - Imagem ${currentImageIndex + 1}`}
                    className={`block max-h-[calc(100vh-140px)] max-w-[95vw] object-contain transition-opacity duration-300 ${mainImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    key={`fullscreen-${car.id}-${car.images[currentImageIndex]?.id ?? "no-image"}`} // Key update
                  />
                </div>
                {/* Navigation buttons */}
                {car.images.length > 1 && ( // Only show if multiple images
                  <>
                    <button
                      onClick={() => handleImageNavigation("prev")}
                      className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70" // Increased z-index
                      aria-label="Imagem anterior"
                    >
                      <ChevronLeft className="h-8 w-8" />
                    </button>

                    <button
                      onClick={() => handleImageNavigation("next")}
                      className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70" // Increased z-index
                      aria-label="Próxima imagem"
                    >
                      <ChevronRight className="h-8 w-8" />
                    </button>
                  </>
                )}
                {/* Thumbnails at bottom */}
                {car.images.length > 1 && ( // Only show if multiple images
                  <div className="absolute bottom-0 left-0 right-0 z-10 flex h-[100px] items-center justify-center overflow-x-auto bg-gradient-to-t from-black/80 via-black/50 to-transparent px-4 py-4">
                    <div className="flex space-x-2">
                      {car.images.map((img, index) => {
                        const thumbUrl = `${getCdnCarImgSrc(img.objectKey)}`;
                        const isSelected = currentImageIndex === index;
                        return (
                          <button
                            key={img.id}
                            onClick={() => {
                              const fullUrl = `${getCdnCarImgSrc(img.objectKey)}`;
                              setMainImage(fullUrl);
                              setCurrentImageIndex(index);
                              setMainImageLoaded(false); // Reset loading state
                            }}
                            className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded border-2 transition-all ${
                              isSelected
                                ? "scale-105 border-primary opacity-100"
                                : "border-transparent opacity-60 hover:opacity-90"
                            }`}
                            aria-label={`Ver imagem ${index + 1}`}
                            aria-current={isSelected ? "true" : "false"}
                          >
                            <Img
                              src={thumbUrl}
                              width={170}
                              height={128}
                              alt={`Miniatura ${index + 1}`}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Charging stations section */}
      {/* ... unchanged charging stations ... */}
      {chargingStations.length > 0 && (
        <section className="bg-gray-50 py-12 dark:bg-gray-900/50">
          <div className="container">
            <div className="mb-8">
              <h2 className="mb-4 text-center text-3xl font-bold">
                Estações de Carregamento Próximas
              </h2>
              <p className="mx-auto max-w-2xl text-center text-foreground/70">
                Carregue seu veículo em estações próximas a{" "}
                {formatText(car.city)}, {formatState(car.state)}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {chargingStations.map((station) => (
                <Link
                  key={station.id}
                  to={`/estacoes/${station.url}${station.id}`}
                  prefetch="intent"
                  className="group overflow-hidden rounded-xl bg-white shadow transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:bg-gray-800" // Added group and hover effect
                >
                  <div className="p-5">
                    <h3 className="mb-1 text-lg font-bold transition-colors group-hover:text-primary">
                      {station.name}
                    </h3>{" "}
                    {/* Hover effect on title */}
                    <p className="mb-3 line-clamp-2 text-sm text-foreground/70">
                      {" "}
                      {/* Line clamp address */}
                      {station.address}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center">
                        <BatteryFull className="mr-1 h-4 w-4 text-primary" />
                        <span>
                          {station.stations.flatMap((s) => s.outlets).length}{" "}
                          {station.stations.flatMap((s) => s.outlets).length ===
                          1
                            ? "ponto"
                            : "pontos"}{" "}
                          {/* Pluralization */}
                        </span>
                      </div>
                      {station.averagePower ? (
                        <div className="flex items-center">
                          <Zap className="mr-1 h-4 w-4 text-primary" />
                          <span>
                            {station.averagePower % 1 === 0
                              ? station.averagePower
                              : station.averagePower.toFixed(1)}{" "}
                            {/* Format power */}
                            kW
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {chargingStations.length > 0 && (
              <div className="mt-8 text-center">
                <Link
                  to={`/estacoes?cidade=${encodeURIComponent(car.city || "")}&estado=${encodeURIComponent(car.state || "")}`}
                  prefetch="intent"
                  className="inline-flex" // Changed from flex w-full
                >
                  <Button
                    variant="outline" // Changed variant
                    size="lg" // Adjusted size
                  >
                    Ver Todas As Estações em {formatText(car.city)}
                    <ChevronRight className="ml-2 h-4 w-4" /> {/* Added icon */}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      {/* ... unchanged CTA ... */}
      <section className="bg-primary py-16 text-white">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold">
              Quer vender seu carro elétrico?
            </h2>
            <p className="mb-8 text-white/80">
              Anuncie gratuitamente seu veículo na maior plataforma brasileira
              de carros elétricos e híbridos plug-in.
            </p>
            <Link
              to="/anuncios"
              className="inline-block rounded-lg bg-white px-8 py-3 font-bold text-primary transition-colors hover:bg-gray-100"
            >
              Anunciar agora
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}